import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Trash2 } from "lucide-react";
import { useACPP, GRADE_ORDER } from "../ACPPContext";
import {
  ModuleCard, ModuleHeader, SectionDivider, SoftPanel, InfoBanner,
  MetricCard, Grid4, TableChip, BtnRow, BtnPrimary, BtnSecondary, BtnLight,
  FormInput, FormTextarea, HelpText, EmptyState, TableWrap, Th, Td,
} from "../components/ui";
import ActionIconButton from "../../../Components/ActionIconButton";

const TYPE_OPTIONS = ["Core Subject","Elective Subject","Co-Curricular Activity","Club","Sport","Vocational Area"];
const CATEGORIES = ["Academic","Co-Curricular","Vocational"];

function mkId() { return `row_${Date.now()}_${Math.random().toString(36).slice(2,7)}`; }

function SubjectRow({ row, gradeOptions, onUpdate, onDelete, readOnly = false }) {
  const update = (field, val) => onUpdate(row.id, field, val);

  const toggleCategory = (cat) => {
    const cats = row.categories.includes(cat)
      ? row.categories.filter(c => c !== cat)
      : [...row.categories, cat];
    update("categories", cats);
  };

  const handleTypeChange = (e) => {
    const vals = Array.from(e.target.selectedOptions).map(o => o.value);
    update("types", vals);
  };

  const handleGradeChange = (e) => {
    const vals = Array.from(e.target.selectedOptions).map(o => o.value);
    update("grades", vals);
  };

  const catStyles = {
    "Academic": row.categories.includes("Academic")
      ? "bg-[#fff4e8] border-[#ffd8b5] text-[#b85d00]"
      : "bg-[#f0f6ff] border-[#d9e7f7] text-[#4a6fa5]",
    "Co-Curricular": row.categories.includes("Co-Curricular")
      ? "bg-[#eefbf1] border-[#cce8d5] text-[#22784b]"
      : "bg-[#f0f6ff] border-[#d9e7f7] text-[#4a6fa5]",
    "Vocational": row.categories.includes("Vocational")
      ? "bg-[#f5f0ff] border-[#dacafc] text-[#6a47c7]"
      : "bg-[#f0f6ff] border-[#d9e7f7] text-[#4a6fa5]",
  };

  return (
    <tr>
      <Td>{row.rowNum}</Td>
      <Td>
        <FormInput
          value={row.subjectName}
          disabled={readOnly}
          onChange={e => update("subjectName", e.target.value)}
          placeholder="Example: Science / Music / Football"
        />
      </Td>
      <Td>
        <select
          multiple
          value={row.grades}
          disabled={readOnly}
          onChange={handleGradeChange}
          className="w-full border border-[#cad7e7] rounded-xl bg-white text-[12px] p-2 min-h-[80px] outline-none focus:border-[#1565c0]"
        >
          {gradeOptions.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        <HelpText>Select one or more grades.</HelpText>
      </Td>
      <Td>
        <div className="flex flex-wrap gap-1.5 mb-1">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              type="button"
              disabled={readOnly}
              onClick={() => toggleCategory(cat)}
              className={`px-2.5 py-[5px] rounded-full text-[11px] font-semibold cursor-pointer border transition-all ${catStyles[cat]}`}
            >
              {cat}
            </button>
          ))}
        </div>
        <HelpText>Choose one or more categories.</HelpText>
      </Td>
      <Td>
        <select
          multiple
          value={row.types}
          disabled={readOnly}
          onChange={handleTypeChange}
          className="w-full border border-[#cad7e7] rounded-xl bg-white text-[12px] p-2 min-h-[80px] outline-none focus:border-[#1565c0]"
        >
          {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <HelpText>Select one or more types.</HelpText>
      </Td>
      <Td>
        <FormInput
          value={row.customType}
          disabled={readOnly}
          onChange={e => update("customType", e.target.value)}
          placeholder="Add custom type"
        />
        <HelpText>Optional custom school-specific type.</HelpText>
      </Td>
      <Td>
        <FormTextarea
          value={row.note}
          disabled={readOnly}
          onChange={e => update("note", e.target.value)}
          placeholder="Optional note"
          rows={2}
        />
        <div className="mt-2">
          <ActionIconButton icon={Trash2} tone="delete" title="Delete" onClick={() => onDelete(row.id)} disabled={readOnly} />
        </div>
      </Td>
    </tr>
  );
}

export default function Module4AcademicPlanning() {
  const {
    selectedGrades,
    subjectMappings,
    module4Loading,
    module4LoadError,
    canEditModules1to5,
    loadModule4AcademicPlanning,
    saveModule4AcademicPlanning,
    resetModule4AcademicPlanning,
  } = useACPP();
  const [rows, setRows] = useState([]);

  const gradeOptions = selectedGrades;
  const hasSelectedGrades = gradeOptions.length > 0;

  const normalizeMappings = useCallback((list) => {
    const dedupe = (items) => {
      const seen = new Set();
      const out = [];
      (items || []).forEach((item) => {
        const clean = String(item ?? "").trim();
        if (clean && !seen.has(clean)) {
          seen.add(clean);
          out.push(clean);
        }
      });
      return out;
    };

    return (list || []).map((item) => {
      const subjectName = String(item?.subjectName ?? "").trim();
      const gradesSet = new Set(dedupe(item?.grades));
      const grades = GRADE_ORDER.filter((g) => gradesSet.has(g));
      const categoriesSet = new Set(dedupe(item?.categories));
      const categories = CATEGORIES.filter((c) => categoriesSet.has(c));
      const types = dedupe(item?.types);
      const customType = String(item?.customType ?? "").trim();
      const finalTypes = dedupe([...types, ...(customType ? [customType] : [])]);
      const note = String(item?.note ?? "").trim();
      return { subjectName, grades, categories, types, customType, finalTypes, note };
    });
  }, []);

  const mappingToRow = useCallback((mapping, index) => ({
    id: mkId(),
    rowNum: index + 1,
    subjectName: mapping.subjectName || "",
    grades: Array.isArray(mapping.grades) ? mapping.grades : [],
    categories: Array.isArray(mapping.categories) ? mapping.categories : [],
    types: Array.isArray(mapping.types) ? mapping.types : [],
    customType: mapping.customType || "",
    note: mapping.note || "",
  }), []);

  useEffect(() => {
    setRows((subjectMappings || []).map((item, index) => mappingToRow(item, index)));
  }, [subjectMappings, mappingToRow]);

  const addRow = (subjectName = "", grades = [], categories = [], types = [], customType = "", note = "") => {
    if (!canEditModules1to5) return;
    setRows(prev => {
      const newRow = { id: mkId(), rowNum: prev.length + 1, subjectName, grades, categories, types, customType, note };
      return [...prev, newRow];
    });
  };

  const updateRow = useCallback((id, field, val) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: val } : r));
  }, []);

  const deleteRow = useCallback((id) => {
    setRows(prev => {
      const filtered = prev.filter(r => r.id !== id);
      return filtered.map((r, i) => ({ ...r, rowNum: i + 1 }));
    });
  }, []);

  const addSuggested = () => {
    if (!canEditModules1to5) return;
    if (!selectedGrades.length) { alert("Please complete Module 3 first by selecting grades."); return; }
    const grades = [...selectedGrades];
    const seniorGrades = grades.filter(g => ["8","9","10","11","12"].includes(g));
    const midSenior = grades.filter(g => !["BV1","BV2","BV3"].includes(g));
    addRow("English", grades, ["Academic"], ["Core Subject"], "", "");
    addRow("Mathematics", grades.filter(g => !["BV1","BV2","BV3"].includes(g)), ["Academic"], ["Core Subject"], "", "");
    addRow("Science", grades.filter(g => !["BV1","BV2","BV3","1","2"].includes(g)), ["Academic"], ["Core Subject"], "", "");
    addRow("Music", grades, ["Co-Curricular"], ["Co-Curricular Activity"], "", "");
    addRow("Football", midSenior, ["Co-Curricular"], ["Sport"], "", "");
    addRow("Entrepreneurship", seniorGrades, ["Vocational"], ["Vocational Area"], "", "");
  };

  const getMappings = useCallback(() => rows.map((r) => ({
    subjectName: r.subjectName,
    grades: r.grades,
    categories: r.categories,
    types: r.types,
    customType: r.customType,
    finalTypes: [...r.types, ...(r.customType ? [r.customType] : [])],
    note: r.note,
  })), [rows]);

  const localMappings = useMemo(() => normalizeMappings(getMappings()), [getMappings, normalizeMappings]);
  const savedMappings = useMemo(() => normalizeMappings(subjectMappings), [subjectMappings, normalizeMappings]);
  const status = localMappings.length === 0 && savedMappings.length === 0
    ? "Pending"
    : JSON.stringify(localMappings) === JSON.stringify(savedMappings)
      ? "Saved"
      : "In Progress";

  const save = async () => {
    if (!canEditModules1to5) return;
    if (!selectedGrades.length) { alert("Please complete Module 3 first by selecting grades."); return; }
    if (!rows.length) { alert("Please add at least one entry."); return; }
    const invalid = rows.find(r => !r.subjectName || !r.grades.length || !r.categories.length || (r.types.length === 0 && !r.customType));
    if (invalid) { alert("Each row must include a subject/offering name, at least one grade, at least one category, and at least one type or custom type."); return; }
    await saveModule4AcademicPlanning(getMappings());
    alert("Academic Planning Stage saved successfully.");
  };

  const reset = async () => {
    if (!canEditModules1to5) return;
    setRows([]);
    await resetModule4AcademicPlanning();
  };

  const mappings = localMappings;
  const uniqueSubjects = [...new Set(mappings.map(m => m.subjectName).filter(Boolean))];
  const gradesCovered = [...new Set(mappings.flatMap(m => m.grades))];

  // Build planning matrix
  const matrixGrades = selectedGrades;

  const catColor = (cat) => cat === "Academic" ? "orange" : cat === "Co-Curricular" ? "green" : "purple";

  return (
    <ModuleCard id="module4">
      <section className="mb-6">
        <div className="text-center px-3 py-2">
          <h3 className="text-4xl font-bold text-[#D67C2C] mb-3">Academic Planning Stage</h3>
          <p className="text-[#1D3469] text-lg font-medium max-w-4xl mx-auto leading-[1.45]">
            Build the grade-wise subject and offering map for this ACPP year. This setup drives ownership, workflow, and all downstream modules.
          </p>
        </div>
      </section>

      <ModuleHeader
        step="Module 4"
        title="Academic Planning Stage"
        description="Map the school's academic and non-academic offerings in a structured way. You can assign one subject or offering to multiple grades, tag it with a category, select multiple school-relevant types, and add a custom type where needed."
        showStatus={false}
      />

      <InfoBanner>
        Use this stage to define what is actually offered in the school across grades. This includes academic subjects, co-curricular areas, sports, clubs, and vocational offerings. Once entries are added, the module will automatically generate grade-wise and subject-wise planning tables below.
      </InfoBanner>
      {module4LoadError ? (
        <HelpText className="text-[#b42318]">{module4LoadError}</HelpText>
      ) : null}
      {!hasSelectedGrades ? (
        <HelpText className="text-[#b42318]">
          Module 3 has no saved grades for this ACPP year. Save grades in Module 3 before working on Module 4.
        </HelpText>
      ) : null}

      <SectionDivider />

      <Grid4>
        <MetricCard label="Total Planning Rows" value={rows.length} meta="Total entries created in this stage" />
        <MetricCard label="Unique Subjects / Offerings" value={uniqueSubjects.length} meta="Distinct names added so far" />
        <MetricCard label="Grades Covered" value={gradesCovered.length} meta="Grades currently mapped in planning" />
        <MetricCard label="Status" value={status} meta="Current module save status" />
      </Grid4>

      <SectionDivider />

      <SoftPanel title="Planning Entry Table">
        <TableWrap>
          <thead>
            <tr>
              <Th style={{ width: "6%" }}>#</Th>
              <Th style={{ width: "18%" }}>Subject / Offering Name</Th>
              <Th style={{ width: "17%" }}>Grades Applicable</Th>
              <Th style={{ width: "16%" }}>Category</Th>
              <Th style={{ width: "18%" }}>Type</Th>
              <Th style={{ width: "12%" }}>Custom Type</Th>
              <Th style={{ width: "13%" }}>Short Note</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7}><EmptyState>No entries added yet. Start by adding the first subject or offering.</EmptyState></td></tr>
            ) : (
              rows.map(row => (
                <SubjectRow
                  key={row.id}
                  row={row}
                  gradeOptions={gradeOptions}
                  onUpdate={updateRow}
                  onDelete={deleteRow}
                  readOnly={!canEditModules1to5}
                />
              ))
            )}
          </tbody>
        </TableWrap>
        {canEditModules1to5 ? (
          <BtnRow>
            <BtnPrimary onClick={() => addRow()} disabled={module4Loading || !hasSelectedGrades}>Add Row</BtnPrimary>
            <BtnLight onClick={addSuggested} disabled={module4Loading || !hasSelectedGrades}>Add Common School Entries</BtnLight>
            <BtnLight onClick={loadModule4AcademicPlanning} disabled={module4Loading}>Refresh Year Data</BtnLight>
            <BtnSecondary onClick={save} disabled={module4Loading || !hasSelectedGrades}>Save Academic Planning Stage</BtnSecondary>
          </BtnRow>
        ) : null}
      </SoftPanel>

      <SectionDivider />

      <SoftPanel title="Grade vs Subject Planning Matrix">
        <p className="text-[13px] text-[#6b7280] leading-[1.7] m-0 mb-3">This view gives a quick picture of which grades have which subjects or offerings. It helps leadership see the complete spread clearly.</p>
        {uniqueSubjects.length === 0 || matrixGrades.length === 0 ? (
          <EmptyState>Add entries above to generate the grade-subject matrix.</EmptyState>
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <Th>Subject / Offering</Th>
                {matrixGrades.map(g => <Th key={g}>{g}</Th>)}
              </tr>
            </thead>
            <tbody>
              {uniqueSubjects.map(subject => {
                const subjectRows = mappings.filter(m => m.subjectName === subject);
                const cats = [...new Set(subjectRows.flatMap(m => m.categories))];
                const types = [...new Set(subjectRows.flatMap(m => m.finalTypes))];
                return (
                  <tr key={subject}>
                    <Td>
                      <span className="font-bold text-[13px] block mb-1">{subject}</span>
                      <div className="flex flex-wrap gap-1">
                        {cats.map(c => <TableChip key={c} color={catColor(c)}>{c}</TableChip>)}
                        {types.map(t => <TableChip key={t}>{t}</TableChip>)}
                      </div>
                    </Td>
                    {matrixGrades.map(grade => {
                      const exists = subjectRows.some(m => m.grades.includes(grade));
                      return (
                        <Td key={grade} className={exists ? "bg-[#eefbf1] text-[#22784b] font-bold text-center" : "bg-[#fafafa] text-[#ccc] text-center"}>
                          {exists ? "✓" : "—"}
                        </Td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </TableWrap>
        )}
      </SoftPanel>

    </ModuleCard>
  );
}
